import React from 'react';
import styled from 'styled-components';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 40px;
  background: #fafafa;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  text-align: center;
  max-width: 600px;
  margin: 20px auto;
`;

const ErrorIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #fef5e7 0%, #fed7aa 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  svg {
    width: 40px;
    height: 40px;
    color: #ea580c;
  }
`;

const ErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 24px 0;
  max-width: 400px;
`;

const ErrorDetails = styled.details`
  margin: 16px 0;
  text-align: left;
  width: 100%;
  max-width: 500px;

  summary {
    cursor: pointer;
    font-size: 14px;
    color: #6b7280;
    padding: 8px;
    background: #f9fafb;
    border-radius: 6px;
    margin-bottom: 8px;
  }

  pre {
    background: #f3f4f6;
    padding: 12px;
    border-radius: 6px;
    font-size: 12px;
    color: #374151;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.primary ? `
    background: linear-gradient(135deg, #1A8B8B 0%, #0D6B6B 100%);
    color: white;
    border: none;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(26, 139, 139, 0.3);
    }
  ` : `
    background: white;
    color: #374151;
    border: 1px solid #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  `}

  svg {
    width: 16px;
    height: 16px;
  }
`;

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Update state with error details
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Optional: Report error to monitoring service
    // reportError(error, errorInfo);
  }

  handleRetry = () => {
    // Clear error state to retry rendering
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  handleGoHome = () => {
    // Reload the page to start fresh
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <ErrorContainer>
          <ErrorIcon>
            <AlertTriangle />
          </ErrorIcon>
          
          <ErrorTitle>Something went wrong</ErrorTitle>
          
          <ErrorMessage>
            We're sorry, but something unexpected happened. This is usually a temporary issue.
            You can try refreshing the page or continue using other features.
          </ErrorMessage>

          <ActionButtons>
            <ActionButton primary onClick={this.handleRetry}>
              <RefreshCw />
              Try Again
            </ActionButton>
            <ActionButton onClick={this.handleGoHome}>
              <Home />
              Refresh Page
            </ActionButton>
          </ActionButtons>

          {/* Show error details in development */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <summary>Error Details (Development)</summary>
              <pre>
                <strong>Error:</strong> {this.state.error.toString()}
                {this.state.errorInfo && (
                  <>
                    <br /><br />
                    <strong>Component Stack:</strong>
                    {this.state.errorInfo.componentStack}
                  </>
                )}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

export default ErrorBoundary;